import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';

// ── Types ─────────────────────────────────────────────────────────────

export type InsightStatus = 'open' | 'in_progress' | 'resolved' | 'dismissed';
export type InsightConfidence = 'low' | 'medium' | 'high';
export type InsightTrend = 'improving' | 'stable' | 'worsening';

export interface InsightMetrics {
  primary_value: number | null;
  primary_label: string;
  primary_value_initial?: number | null;
  primary_trend?: InsightTrend;
  secondary_value: number | null;
  secondary_label: string | null;
  tertiary_value?: number | null;
  tertiary_label?: string | null;
}

export interface InsightEntities {
  persona_ids?: string[];
  question_ids?: string[];
  competitor_names?: string[];
  source_urls?: string[];
  model_names?: string[];
  gsc_queries?: string[];
  landing_pages?: string[];
  questions?: string[];
  persona_labels?: Record<string, string>;
  // geo_vs_organic_mismatch enrichment
  clicks?: number;
  impressions?: number;
  matched_persona?: string;
  direction?: string;
  // editorial_outreach_opportunity enrichment
  source_domains?: string[];
  source_types?: string[];
}

export interface InsightCTA {
  type: 'create_content' | 'create_content_multi' | 'view_prompt' | 'view_persona' | 'view_sources' | 'external_link' | null;
  target_id: string | null;
  label: string;
}

export type InsightCategory = 'visibility' | 'competitive' | 'content' | 'gsc_ga4';
export type InsightSeverity = 'critical' | 'high' | 'medium' | 'positive';
export type InsightType =
  | 'visibility_gap'
  | 'sentiment_divergence'
  | 'source_authority_gap'
  | 'model_weakness'
  | 'competitor_displacement'
  | 'content_priority'
  | 'cross_persona_pattern'
  | 'trend_alert'
  | 'traffic_at_risk'
  | 'query_opportunity_gap'
  | 'revenue_impact'
  | 'geo_vs_organic_mismatch'
  | 'editorial_outreach_opportunity'
  | 'web_influenceability_alert'
  | 'reddit_opportunity';

export interface Insight {
  id: string;
  type: InsightType;
  category: InsightCategory;
  status: InsightStatus;
  severity: InsightSeverity;
  confidence: InsightConfidence;
  priority_score: number;
  title: string;
  body: string;
  action: string;
  metrics: InsightMetrics;
  entities: InsightEntities;
  cta: InsightCTA;
  // Lifecycle fields
  created_at: string;
  last_updated: string;
  status_changed_at: string | null;
  status_changed_by: 'user' | 'system' | null;
  resolved_at: string | null;
  resolved_reason: 'auto' | 'user' | null;
  resolve_message: string | null;
}

export interface InsightsData {
  domain_id: string;
  initialized_at: string | null;
  last_updated: string | null;
  snapshot_count: number;
  has_gsc_data: boolean;
  has_ga4_data: boolean;
  items: Insight[];
  // Legacy aliases (backward compat)
  generated_at?: string | null;
  insight_count?: number;
  is_first_analysis?: boolean;
  has_gsc_insights?: boolean;
  has_ga4_insights?: boolean;
}

type LoadingStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface InsightsState {
  dataByDomain: Record<string, InsightsData>;
  statusByDomain: Record<string, LoadingStatus>;
  errorByDomain: Record<string, string | undefined>;
}

const initialState: InsightsState = {
  dataByDomain: {},
  statusByDomain: {},
  errorByDomain: {},
};

// ── Async Thunks ──────────────────────────────────────────────────────

export const fetchInsights = createAsyncThunk<
  InsightsData,
  string, // domainId
  { rejectValue: string }
>(
  'insights/fetchInsights',
  async (domainId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/v1/domains/${domainId}/insights`);
      return res.data as InsightsData;
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to fetch insights';
      return rejectWithValue(message);
    }
  },
  {
    // Built-in caching: skip fetch if already loaded
    condition: (domainId, { getState }) => {
      const state = (getState() as { insights: InsightsState }).insights;
      const status = state.statusByDomain[domainId];
      return status !== 'loading' && status !== 'succeeded';
    },
  },
);

export const updateInsightStatus = createAsyncThunk<
  { domainId: string; insightId: string; status: InsightStatus },
  { domainId: string; insightId: string; status: InsightStatus },
  { rejectValue: string }
>(
  'insights/updateInsightStatus',
  async ({ domainId, insightId, status }, { rejectWithValue }) => {
    try {
      await apiClient.patch(
        `/v1/domains/${domainId}/insights/${insightId}`,
        { status },
      );
      return { domainId, insightId, status };
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to update insight status';
      return rejectWithValue(message);
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────

const insightsSlice = createSlice({
  name: 'insights',
  initialState,
  reducers: {
    clearInsightsForDomain: (state, action) => {
      const id = action.payload as string;
      delete state.dataByDomain[id];
      delete state.statusByDomain[id];
      delete state.errorByDomain[id];
    },
    clearAllInsights: (state) => {
      state.dataByDomain = {};
      state.statusByDomain = {};
      state.errorByDomain = {};
    },
    // Allow re-fetching by resetting status to idle
    invalidateInsights: (state, action) => {
      const id = action.payload as string;
      state.statusByDomain[id] = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInsights.pending, (state, action) => {
        state.statusByDomain[action.meta.arg] = 'loading';
        state.errorByDomain[action.meta.arg] = undefined;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        const domainId = action.meta.arg;
        state.statusByDomain[domainId] = 'succeeded';
        state.dataByDomain[domainId] = action.payload;
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.statusByDomain[action.meta.arg] = 'failed';
        state.errorByDomain[action.meta.arg] = action.payload || 'Unknown error';
      })
      // ── updateInsightStatus ──
      .addCase(updateInsightStatus.fulfilled, (state, action) => {
        const { domainId, insightId, status } = action.payload;
        const data = state.dataByDomain[domainId];
        if (data) {
          const item = data.items.find((i) => i.id === insightId);
          if (item) {
            item.status = status;
            item.status_changed_at = new Date().toISOString();
            item.status_changed_by = 'user';
            if (status === 'resolved' || status === 'dismissed') {
              item.resolved_at = new Date().toISOString();
              item.resolved_reason = 'user';
            }
          }
        }
      });
  },
});

export const { clearInsightsForDomain, clearAllInsights, invalidateInsights } = insightsSlice.actions;
export default insightsSlice.reducer;
